<!-- Difficulty, sound, controls, and the scores worth keeping. -->
<template>
  <IonPage>
    <IonHeader :translucent="true">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton default-href="/tabs/game" text="Back" />
        </IonButtons>
        <IonTitle>Settings</IonTitle>
      </IonToolbar>
    </IonHeader>

    <IonContent class="rm-content" :fullscreen="true">
      <div class="rm-page doc">
        <h2>Difficulty</h2>
        <p class="hint">Changing this starts the next run. It does not affect one in progress.</p>
        <div class="picker" role="radiogroup" aria-label="Difficulty">
          <button
            v-for="d in DIFFICULTIES"
            :key="d.id"
            type="button"
            role="radio"
            :aria-checked="difficulty === d.id"
            :class="{ on: difficulty === d.id }"
            @click="difficulty = d.id"
          >
            <b>{{ d.name }}</b>
            <span>{{ d.blurb }}</span>
          </button>
        </div>

        <h2>Pace</h2>
        <p class="hint">
          Slows or quickens everything by the same amount &mdash; Nick, every Raf,
          the waves and the fruit-pie timer. The game is unchanged; only the
          tempo is.
        </p>
        <div class="picker" role="radiogroup" aria-label="Pace">
          <button
            v-for="p in SPEEDS" :key="p.id" type="button" role="radio"
            :aria-checked="speed === p.id" :class="{ on: speed === p.id }"
            @click="speed = p.id"
          >
            <b>{{ p.name }}</b>
            <span>{{ p.blurb }}</span>
          </button>
        </div>

        <h2>Faces</h2>
        <p class="hint">
          Nick and Raf&rsquo;s pictures are chosen on the home screen now, with the
          rest of the setup for a run &mdash; the difficulty, the pace and the map.
        </p>
        <button type="button" class="linkish" @click="router.push('/tabs/game')">
          Go to the home screen
        </button>

        <h2>Playing</h2>
        <div class="rows">
          <label>
            <span><b>Sound</b><small>Bites, the pie, and the bad news.</small></span>
            <input v-model="soundOn" type="checkbox" role="switch">
          </label>
          <label>
            <span><b>On-screen pad</b><small>Swiping the maze works either way.</small></span>
            <input v-model="padOn" type="checkbox" role="switch">
          </label>
          <label>
            <span>
              <b>Vegan pizza</b>
              <small>Basil and olive instead of pepperoni. Scores the same.</small>
            </span>
            <input v-model="vegan" type="checkbox" role="switch">
          </label>
        </div>

        <h2>Best scores</h2>
        <p class="hint">
          Kept per setup. Fifty Rafs on the small map is not the same game as one
          Raf on the standard map, and one column for both makes the number mean
          nothing.
        </p>
        <div v-if="runs.length" class="rows">
          <div v-for="r in runs" :key="r.key" class="score">
            <span class="what">
              {{ r.difficulty }}
              <small>
                {{ r.board }} &middot; {{ r.ghosts }} Raf{{ r.ghosts === 1 ? '' : 's' }}<template
                  v-if="r.pies"
                > &middot; +{{ r.pies }} pies</template> &middot; {{ r.speed }}
              </small>
            </span>
            <span class="n">
              {{ r.score.toLocaleString() }}
              <small>level {{ r.level }}</small>
            </span>
          </div>
        </div>
        <p v-else class="hint">Nothing yet. Go and eat some pizza.</p>

        <button v-if="runs.length" type="button" class="danger" @click="clear">
          {{ confirming ? 'Tap again to erase them' : 'Clear best scores' }}
        </button>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton,
} from '@ionic/vue';
import {
  DIFFICULTIES, SPEEDS, difficulty, speed, soundOn, padOn, vegan, best, playedSetups,
} from '../store.js';





const router = useRouter();
const confirming = ref(false);
const runs = computed(() => playedSetups());


// Two taps rather than a modal. Erasing a high score is worth a beat of
// hesitation, and not worth interrupting the screen for.
function clear() {
  if (!confirming.value) {
    confirming.value = true;
    setTimeout(() => { confirming.value = false; }, 4000);
    return;
  }
  best.value = {};
  confirming.value = false;
}
</script>

<style scoped>
.doc { padding: 8px 20px 40px; max-width: 34rem; margin: 0 auto; }

h2 {
  font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--rm-cyan); margin: 26px 0 8px;
}
h2:first-child { margin-top: 8px; }
.private { color: var(--rm-cyan); opacity: 0.85; }

.faces { display: flex; gap: 10px; }
.face {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 7px;
  padding: 13px 10px; background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 10px;
}
.thumb {
  width: 62px; height: 62px; border-radius: 50%; overflow: hidden;
  display: grid; place-items: center; background: #0a1122; border: 1px solid var(--rm-edge);
}
.thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.thumb.empty span { font-size: 10px; color: var(--rm-dim); }
.face b { font-size: 13px; font-weight: 700; }
.pick {
  font-size: 12px; font-weight: 500; padding: 7px 15px; cursor: pointer;
  color: var(--rm-bg); background: var(--rm-cyan); border-radius: 8px;
}
.pick input { display: none; }
.pick:focus-within { outline: 2px solid var(--rm-cyan); outline-offset: 3px; }
.drop {
  font: inherit; font-size: 11px; color: var(--rm-dim); background: none;
  border: 0; padding: 2px 4px; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
}
.linkish {
  font: inherit; font-size: 13px; color: var(--rm-cyan); background: none;
  border: 0; padding: 4px 0; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
}
.hint { font-size: 12px; line-height: 1.5; color: var(--rm-dim); margin: 0 0 12px; }

.picker { display: flex; flex-direction: column; gap: 8px; }
.picker button {
  font: inherit; text-align: left; display: flex; flex-direction: column; gap: 3px;
  padding: 13px 15px; cursor: pointer; color: var(--rm-ink);
  background: var(--rm-panel); border: 1px solid var(--rm-edge); border-radius: 10px;
  transition: border-color 140ms, background 140ms;
}
.picker button.on { border-color: var(--rm-cyan); background: #10203a; }
.picker b { font-size: 14px; font-weight: 700; }
.picker button.on b { color: var(--rm-cyan); }
.picker span { font-size: 12px; line-height: 1.45; color: var(--rm-dim); }
.picker em { font-style: normal; font-size: 11px; color: var(--rm-gold); margin-top: 2px; }

.rows { display: flex; flex-direction: column; }
.rows label, .score {
  display: flex; justify-content: space-between; align-items: center; gap: 18px;
  padding: 13px 0; border-bottom: 1px solid var(--rm-edge);
}
.rows label span { display: flex; flex-direction: column; gap: 2px; }
.rows b { font-size: 14px; font-weight: 500; color: var(--rm-ink); }
.rows small { font-size: 11px; color: var(--rm-dim); }
.score span:first-child { font-size: 14px; color: var(--rm-ink); }
.what { display: flex; flex-direction: column; gap: 2px; text-align: left; }
.what small { font-size: 11px; color: var(--rm-dim); }
.n { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
h3.sub {
  font-size: 13px; font-weight: 700; color: var(--rm-ink);
  margin: 20px 0 6px;
}
.style { margin-top: 4px; }
.style button.idle { opacity: 0.7; }
.what { display: flex; flex-direction: column; gap: 2px; text-align: left; }
.what small { font-size: 11px; color: var(--rm-dim); }
.n { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
h3.sub {
  font-size: 13px; font-weight: 700; color: var(--rm-ink);
  margin: 20px 0 6px;
}
.style { margin-top: 4px; }
.style button.idle { opacity: 0.7; }
.n { font-size: 15px; font-weight: 700; color: var(--rm-gold); font-variant-numeric: tabular-nums; }
.n small { font-weight: 400; font-size: 11px; color: var(--rm-dim); }
.none { color: var(--rm-dim); }

input[type='checkbox'] {
  appearance: none; width: 44px; height: 26px; flex: none; cursor: pointer;
  border-radius: 13px; background: #1b2745; position: relative;
  transition: background 160ms cubic-bezier(0.16, 1, 0.3, 1);
}
input[type='checkbox']::after {
  content: ''; position: absolute; top: 3px; left: 3px; width: 20px; height: 20px;
  border-radius: 50%; background: var(--rm-dim);
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), background 160ms;
}
input[type='checkbox']:checked { background: var(--rm-cyan); }
input[type='checkbox']:checked::after { transform: translateX(18px); background: var(--rm-bg); }
input:focus-visible, button:focus-visible { outline: 2px solid var(--rm-cyan); outline-offset: 3px; }

.danger {
  font: inherit; font-size: 13px; margin-top: 22px; padding: 11px 18px; width: 100%;
  cursor: pointer; color: var(--rm-pink); background: none;
  border: 1px solid rgb(255 45 149 / 40%); border-radius: 9px;
}
.danger:active { background: rgb(255 45 149 / 10%); }

@media (prefers-reduced-motion: reduce) {
  input[type='checkbox'], input[type='checkbox']::after { transition: none; }
}
</style>
